import React, { useState } from 'react';
import {
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBListGroup,
  MDBListGroupItem,
  MDBBtn,
  MDBInput,
  MDBIcon,
  MDBBadge,
  MDBSpinner
} from 'mdb-react-ui-kit';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useEventProducts } from './hooks/useEventProducts';
import { transformMoney } from '../../../utils/money';
import { getLocalizedText } from '../../../utils/localizedText';
import TimeRangePriceModal, { TimeRangePriceButton } from '../../products/TimeRangePriceModal';

const MarketingTab = ({ eventId }) => {
  const { t } = useTranslation();
  const {
    isLoading,
    eventProducts,
    availableProducts,
    addProductToEvent,
    removeProductFromEvent,
    updateEventProduct,
    reorderProducts
  } = useEventProducts(eventId, 'marketing');

  const [productPrices, setProductPrices] = useState({});
  const [productStocks, setProductStocks] = useState({});
  const [selectedEventProduct, setSelectedEventProduct] = useState(null);
  const [timeRangePriceModalOpen, setTimeRangePriceModalOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <MDBSpinner />
        <div className="mt-2">{t('Loading')}</div>
      </div>
    );
  }

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(eventProducts);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    reorderProducts(items);
  };

  const handleAddProduct = (productId) => {
    const price = productPrices[productId] || availableProducts.find(p => p.id === productId)?.price;
    const stock = productStocks[productId] || availableProducts.find(p => p.id === productId)?.stock;
    addProductToEvent(productId, price, stock);
  };

  const handlePriceChange = (eventProductId, newPrice) => {
    updateEventProduct(eventProductId, { price: parseFloat(newPrice) });
  };

  const handleStockChange = (eventProductId, newStock) => {
    updateEventProduct(eventProductId, { stock: parseInt(newStock) });
  };

  const handleUpdateEventProductWithTimeRange = (eventProductId, price, stock, timeRangePrices) => {
    updateEventProduct(eventProductId, { price, stock, timeRangePrices });
  };

  // Filter out products that are already in the event
  const availableProductsFiltered = availableProducts.filter(
    product => !eventProducts.some(ep => ep.product.id === product.id)
  );

  return (
    <>
      <MDBRow className="mb-4">
      <MDBCol size="12" lg="6">
        <MDBCard className="h-100">
          <MDBCardBody>
            <MDBCardTitle>{t('Available Products')}</MDBCardTitle>
            <MDBListGroup style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {availableProductsFiltered.map((product) => (
                <MDBListGroupItem key={product.id} className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center flex-grow-1">
                    {product.image?.main?.fileName ? (
                      <img
                        src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/ProductImages/${product.image.main.fileName}`}
                        style={{ height: '40px', width: '40px', objectFit: 'contain' }}
                        alt=""
                        className="me-2"
                      />
                    ) : (
                      <MDBIcon
                        fas
                        icon={product.icon || 'bullhorn'}
                        size="2x"
                        className="me-2"
                        style={{ width: '40px', textAlign: 'center' }}
                      />
                    )}
                    <div className="flex-grow-1">
                      <div className="fw-bold">{getLocalizedText(product.name)}</div>
                      <div className="text-muted small mb-2">{product.sku}</div>
                      <div className="d-flex align-items-center">
                        <MDBInput
                          type="number"
                          label={t("Price")}
                          defaultValue={product.price}
                          onChange={(e) => setProductPrices({ ...productPrices, [product.id]: e.target.value })}
                        >
                          <i className="fas fa-euro-sign trailing"></i>
                        </MDBInput>
                        <div style={{ width: '40px' }}></div> {/* Spacer for missing TimeRangeButton */}
                        <MDBInput
                          type="number"
                          label={t("Stock")}
                          defaultValue={product.stock}
                          onChange={(e) => setProductStocks({ ...productStocks, [product.id]: e.target.value })}
                        />
                        <MDBBtn
                          color="primary"
                          className="ms-2"
                          style={{width: '250px'}}
                          size="sm"
                          outline
                          onClick={() => handleAddProduct(product.id)}
                        >
                          {t('Add')}
                        </MDBBtn>
                      </div>
                    </div>
                  </div>
                </MDBListGroupItem>
              ))}
            </MDBListGroup>
          </MDBCardBody>
        </MDBCard>
      </MDBCol>

      <MDBCol size="12" lg="6">
        <MDBCard className="h-100">
          <MDBCardBody>
            <MDBCardTitle>
              {t('Marketing Products')}
              <MDBBadge className="ms-2" color="primary">{eventProducts.length}</MDBBadge>
            </MDBCardTitle>
            <p className="text-muted small">{t('Drag to reorder')}</p>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="marketingProducts">
                {(provided) => (
                  <MDBListGroup
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ maxHeight: '600px', overflowY: 'auto' }}
                  >
                    {eventProducts.map((eventProduct, index) => (
                      <Draggable
                        key={eventProduct.id}
                        draggableId={eventProduct.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <MDBListGroupItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`d-flex justify-content-between align-items-center ${
                              snapshot.isDragging ? 'shadow-3' : ''
                            }`}
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'white'
                            }}
                          >
                            <div className="d-flex align-items-center flex-grow-1">
                              <div
                                {...provided.dragHandleProps}
                                className="me-2"
                                style={{ cursor: 'grab' }}
                              >
                                <MDBIcon fas icon="grip-vertical" />
                              </div>
                              {eventProduct.product.image?.main?.fileName ? (
                                <img
                                  src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/ProductImages/${eventProduct.product.image.main.fileName}`}
                                  style={{ height: '40px', width: '40px', objectFit: 'contain' }}
                                  alt=""
                                  className="me-2"
                                />
                              ) : (
                                <MDBIcon
                                  fas
                                  icon={eventProduct.product.icon || 'bullhorn'}
                                  size="2x"
                                  className="me-2"
                                  style={{ width: '40px', textAlign: 'center' }}
                                />
                              )}
                              <div className="flex-grow-1">
                                <div className="fw-bold">{getLocalizedText(eventProduct.product.name)}</div>
                                <div className="text-muted small mb-2">{eventProduct.product.sku}</div>
                                <div className="d-flex align-items-center">
                                  <MDBInput
                                    type="number"
                                    label={t("Price")}
                                    value={eventProduct.price || ''}
                                    onChange={(e) => handlePriceChange(eventProduct.id, e.target.value)}
                                  >
                                    <i className="fas fa-euro-sign trailing"></i>
                                  </MDBInput>
                                  <TimeRangePriceButton
                                    hasTimeRangePrices={eventProduct.timeRangePrices?.length > 0}
                                    onClick={() => {
                                      setSelectedEventProduct(eventProduct);
                                      setTimeRangePriceModalOpen(true);
                                    }}
                                  />
                                  <MDBInput
                                    type="number"
                                    label={t("Stock")}
                                    value={eventProduct.stock || ''}
                                    onChange={(e) => handleStockChange(eventProduct.id, e.target.value)}
                                  />
                                  <MDBBtn
                                    onClick={() => removeProductFromEvent(eventProduct.id)}
                                    color="danger"
                                    className="ms-2"
                                    style={{width: '250px'}}
                                    size="sm"
                                    outline
                                  >
                                    {t("Remove")}
                                  </MDBBtn>
                                </div>
                              </div>
                            </div>
                          </MDBListGroupItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </MDBListGroup>
                )}
              </Droppable>
            </DragDropContext>
          </MDBCardBody>
        </MDBCard>
      </MDBCol>
    </MDBRow>

    {selectedEventProduct && (
      <TimeRangePriceModal
        isOpen={timeRangePriceModalOpen}
        onClose={() => {
          setTimeRangePriceModalOpen(false);
          setSelectedEventProduct(null);
        }}
        eventProduct={selectedEventProduct}
        onUpdateEventProduct={handleUpdateEventProductWithTimeRange}
      />
    )}
    </>
  );
};

export default MarketingTab;